import circle from '@turf/circle';
import baseLayers, { Radius, removeApproxLocationCircle } from './layers';
import { syncPropertySearchForms } from '../property/search-filters';
import { newFeatureFilter, newPropertyFilter } from './request-filters';
import { Coast, District, Entity, Island, Municipality, Neighbourhood, Province, Region, locationTypes } from '../locations';
import { boundingBoxIsLikePoint, newBoundingBoxEvent, newBoundingBoxOptions } from './bounding-box';
import { isMapMode } from './util';
import { whenFeatureAvailable, whenFeaturesAvailable } from './features';
import { wrapUpdateCall } from '../maps/api';
import { deactiveFeaturesForSource, findFirstCustomLayer, locationSources, removeLayersForSource, setLayer } from './layers';
import { removeRadiusFromMap } from './radius';
import { getFeatureRequestsForArea } from './area';
import LocationFeature from './models/feature';
import { getChildLocationTypeForParent, getParentLocationTypeForChild } from '../locations';
import SearchThisArea from './components/search-this-area';
import PropertySearchFilter, { locationFilters } from '../property/filters';
import Logger from '../logging/logger';

/**
 * @param {MapSearch} ctx - the MapSearch context
 * @param {HTMLElement} from - the HTML form element to sync from
 * @param {HTMLElement} to - the HTML form element to sync to
 */
function handleSearch( ctx, from, to ) {
    return async ( event ) => {
        if ( !isMapMode() ) {
            return;
        }

        await search( ctx, event, {
            from: from,
            to: to,
            ...event.detail || {},
        } );
    };
}

async function search( ctx, event = null, options = {} ) {
    if ( event && event.submitter && event.submitter.dataset.destination && event.submitter.dataset.destination === 'find-your-place' ) {
        return;
    }

    if ( event ) {
        event.preventDefault();
    }

    ctx.setLoading( true );

    options = Object.assign( {}, {
        addArea: true,
        allowReset: true,
        clearMoveFuncs: true,
    }, options );

    let from = options.from;
    let to = options.to;

    syncPropertySearchForms( from, to );

    if ( document.querySelector( '#search-filter-popup.pagepopup__wrapper--open' ) ) {
        ctx.root.dispatchEvent( new CustomEvent( 'twc:search-popup-close', { bubbles: true } ) );
    }

    const bounds = ctx.map.getBounds();
    const boundSW = bounds.getSouthWest().lat.toString() + ',' + bounds.getSouthWest().lng.toString();
    const boundNE = bounds.getNorthEast().lat.toString() + ',' + bounds.getNorthEast().lng.toString();

    let initialSearch = false;

    if ( !from.dataset.initialSearchStarted && !to.dataset.initialSearchStarted ) {
        initialSearch = true;
        from.dataset.initialSearchStarted = 'true';
        to.dataset.initialSearchStarted = 'true';
    }

    document.dispatchEvent( new CustomEvent( 'twc:update-filter-count-badges', {
        bubbles: true,
    } ) );

    let featureTypeToLoad;
    let featureTypeToLoadCount;

    const locationKey = from.querySelector( '[name=locationId]' );
    const locationFreeText = from.querySelector( '[name=locationFreeText]' );
    const topTypeaheadResult = from.querySelector( '#awesomplete_list_1_item_0' );

    if ( locationKey.value.length === 0 && locationFreeText.value.length > 0 && topTypeaheadResult ) {
        topTypeaheadResult.click();

        if ( from.id === 'property-search-form-popup' ) {
            from.dispatchEvent( new CustomEvent( 'twc:mapsearch:form-submit', { bubbles: true } ) );
        }
        return;
    }

    if ( locationKey.value !== 'search-this-area' ) {
        ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:search-this-area:deactivate', {
            bubbles: true,
            detail: {
                search: false,
            },
        } ) );
    }

    if ( ctx.activeComponents.has( 'draw-your-search' ) ) {
        ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:draw-your-search:deactivate', {
            bubbles: true,
            detail: {
                search: false,
            },
        } ) );
    }

    let filter = PropertySearchFilter.fromForm( from );

    if ( filter.anyLocationFilters() ) {
        locationTypes.filter( ( locationType ) => locationType !== Municipality ).forEach( ( locationType ) => {
            if ( ctx.map.getLayer( locationType + ':fill' ) ) {
                ctx.map.removeLayer( locationType + ':fill' );
            }

            if ( ctx.map.getLayer( locationType + ':outline' ) ) {
                ctx.map.removeLayer( locationType + ':outline' );
            }
        } );
    }

    if ( filter.currentLocationID > 0 && !filter.locationValue.startsWith( 'location-filters' ) && filter.anyLocationFilters() ) {
        [ ctx.baseForm, ctx.popupForm ].forEach( ( form ) => {
            locationFilters.all().forEach( ( name ) => {
                form.querySelectorAll( `[name=${name}]` ).forEach( ( input ) => input.value = '' );
            } );
        } );
    }

    filter = PropertySearchFilter.fromForm( from );

    const propertyParams = newPropertyFilter( {
        boundSW: boundSW,
        boundNE: boundNE,
        onlyProperties: false,
        activeComponents: Array.from( ctx.activeComponents ),
    }, from );

    if ( options.currentLocationID > 0 && options.currentLocationType && options.currentLocationType.length > 0 ) {
        propertyParams.set( 'currentLocationID', options.currentLocationID );
        propertyParams.set( 'currentLocationType', options.currentLocationType );
    }

    if ( initialSearch && ctx.options.initialLocation.hasBbox ) {
        propertyParams.set( 'locationBoundSW', ctx.options.initialLocation.bboxSouthWest );
        propertyParams.set( 'locationBoundNE', ctx.options.initialLocation.bboxNorthEast );
    }

    if ( options.locationBoundNE && options.locationBoundSW && options.parentLocations ) {
        propertyParams.set( 'locationBoundSW', options.locationBoundSW );
        propertyParams.set( 'locationBoundNE', options.locationBoundNE );
        propertyParams.set( 'parentLocations', options.parentLocations );
    }

    let isRadiusSearch = ( propertyParams.has( 'radius' ) && parseInt( propertyParams.get( 'radius' ) ) > 0 );
    let isAreaSearch = ( propertyParams.has( 'area' ) && propertyParams.get( 'area' ).length > 0 );

    if ( ctx.previousSearchState === null ) {
        ctx.previousSearchState = propertyParams.toString();
    }

    const previouSearch = new URLSearchParams( ctx.previousSearchState );
    const previousFilter = PropertySearchFilter.fromURLSearchParams( previouSearch );

    let locationChanged = false;
    let fetchFeatures = false;
    let radiusChanged = false;
    let areaChanged = false;
    let fypChanged = previousFilter.anyLocationFilters() && !filter.anyLocationFilters();

    const sameLocation = ( previouSearch.get( 'currentLocationID' ) === propertyParams.get( 'currentLocationID' ) );

    if ( !sameLocation ) {
        locationChanged = true;
    }

    if ( previouSearch.get( 'radius' ) !== propertyParams.get( 'radius' ) ) {
        radiusChanged = true;
    }

    if ( previouSearch.get( 'area' ) !== propertyParams.get( 'area' ) ) {
        areaChanged = true;
    }

    if ( !radiusChanged && !locationChanged && !initialSearch ) {
        propertyParams.delete( 'longitude' );
        propertyParams.delete( 'latitude' );
    }

    if ( locationChanged && options.clearMoveFuncs ) {
        ctx.movefuncs = [];
    }

    const currentLocationHasBounds = (
        ctx.currentLocation &&
        ctx.currentLocation.properties.boundingBoxMaxLat &&
        ctx.currentLocation.properties.boundingBoxMaxLng &&
        ctx.currentLocation.properties.boundingBoxMinLat &&
        ctx.currentLocation.properties.boundingBoxMinLng
    );

    if ( !locationChanged && ctx.currentLocation && currentLocationHasBounds ) {
        const locationBoundSW = ctx.currentLocation.properties.boundingBoxMaxLat + ',' + ctx.currentLocation.properties.boundingBoxMaxLng;
        const locationBoundNE = ctx.currentLocation.properties.boundingBoxMinLat + ',' + ctx.currentLocation.properties.boundingBoxMinLng;
        propertyParams.set( 'locationBoundSW', locationBoundSW );
        propertyParams.set( 'locationBoundNE', locationBoundNE );
    }

    if ( !locationChanged && ctx.currentLocation && ctx.currentLocation.properties.municipalityID && ctx.currentLocation.properties.municipalityID.length > 0 ) {
        let municipalityIDs = ctx.currentLocation.properties.municipalityID;

        if ( typeof municipalityIDs === 'string' ) {
            municipalityIDs = JSON.parse( municipalityIDs );
        }

        if ( municipalityIDs && municipalityIDs.length > 0 ) {
            for ( let i = 0; i < municipalityIDs.length; i++ ) {
                propertyParams.append( 'parentLocations', Municipality + ':' + municipalityIDs[ i ] );
            }
        }
    }

    if ( !propertyParams.get( 'currentLocationID' ) ) {
        fetchFeatures = false;
    }

    if ( !isRadiusSearch ) {
        removeRadiusFromMap( ctx.map );
    }

    if ( !propertyParams.has( 'area' ) || propertyParams.get( 'area' ).length === 0 ) {
        ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:hide-remove-search-area', { bubbles: true } ) );

        ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:remove-area', {
            bubbles: true,
            detail: {
                search: false,
            },
        } ) );
    }

    if ( locationChanged && ctx.map.getLayer( 'approx-location:fill' ) ) {
        ctx.map.removeLayer( 'approx-location:fill' );
    }

    if ( locationChanged && ctx.map.getSource( 'approx-location' ) ) {
        ctx.map.removeSource( 'approx-location' );
    }

    Logger.info( '[map] Property Search Form Submitted', {
        initialSearch,
        isAreaSearch,
        isRadiusSearch,
        locationChanged,
        radiusChanged,
        areaChanged,
        fypChanged,
        options,
        previouSearch: previouSearch.toString(),
        currentSearch: propertyParams.toString(),
    } );

    let updateParams = {
        fetchFeatures: fetchFeatures,
        fetchProperties: true,
        propertySearchURL: ctx.propertySearchURL,
        propertySearchParams: propertyParams,
        expectProperties: true,
    };

    let updateMeta = {
        tasks: [],
        asyncTasks: [],
        focusMapToSearch: !isRadiusSearch && !initialSearch,
        initialSearch: initialSearch,
        addArea: options.addArea,
    };

    if ( isAreaSearch && ( !areaChanged && !initialSearch ) ) {
        updateMeta.focusMapToSearch = false;
        updateMeta.addArea = false;
    }

    if ( isAreaSearch && ctx.activeComponents.has( SearchThisArea.id ) ) {
        updateMeta.focusMapToSearch = false;
        updateMeta.addArea = false;
    }

    if ( 'triggerStateChange' in options ) {
        updateMeta.triggerStateChange = options.triggerStateChange;
    }

    if ( 'focusMapToSearch' in options ) {
        updateMeta.focusMapToSearch = options.focusMapToSearch;
    }

    let selectedLocation;
    let currentLocation;
    const hasLocationSpecified = parseInt( propertyParams.get( 'currentLocationID' ) ) > 0;

    if ( initialSearch && ctx.options.initialLocation.id > 0 ) {
        selectedLocation = ctx.options.initialLocation;
    }

    if ( !initialSearch && hasLocationSpecified ) {
        let location = from.querySelector( '[name=locationId]' ).dataset.location;
        if ( location && location.length > 0 ) {
            selectedLocation = LocationFeature.fromJSON( location );
        }
    }

    if ( hasLocationSpecified && ( locationChanged || fypChanged || initialSearch || radiusChanged || ( areaChanged && !propertyParams.get( 'area' ) ) ) ) {
        let data = {};

        if ( !filter.anyLocationFilters() ) {
            const searchLocationID = parseInt( propertyParams.get( 'currentLocationID' ) );
            const searchLocationType = propertyParams.get( 'currentLocationType' );

            updateParams.expectProperties = true;

            if ( selectedLocation ) {
                featureTypeToLoad = selectedLocation.nextChildFeatureType();
            } else {
                featureTypeToLoad = getChildLocationTypeForParent( searchLocationType );
            }

            updateParams.fetchFeatures = true;
            updateParams.featureSearchURL = new URL( '/' + getChildLocationTypeForParent( searchLocationType ), ctx.apiURL );
            updateParams.featureSearchParams = newFeatureFilter();

            const parentURL = new URL( '/closestLegacyParent', ctx.apiURL );
            parentURL.searchParams.set( 'id', searchLocationID );

            let hasChildren = false;

            if ( initialSearch && ctx.options.initialLocation.hasChildren && ( searchLocationType !== Region && searchLocationType !== Province ) ) {
                hasChildren = true;
                featureTypeToLoad = ctx.options.initialLocation.nextChildFeatureType();
                featureTypeToLoadCount = ctx.options.initialLocation.countOfChildType( featureTypeToLoad );
            }

            const response = await http( parentURL );

            if ( response.status !== 204 ) {
                data = await response.json();
            }

            let siblingParentType = getParentLocationTypeForChild( data.type );

            if ( data.type === Entity && data.keys[ District ] ) {
                siblingParentType = District;
            }

            if ( data.type === Entity && data.keys[ Neighbourhood ] ) {
                siblingParentType = Neighbourhood;
            }

            currentLocation = {
                type: searchLocationType || data.location.type,
                id: searchLocationID,
                data: data,
            };

            if ( data.location && data.location.id > 0 ) {
                selectedLocation = LocationFeature.fromJSON( JSON.stringify( data.location ) );
                featureTypeToLoad = selectedLocation.nextChildFeatureType();
                featureTypeToLoadCount = selectedLocation.countOfChildType( featureTypeToLoad );

                const locationBoundNE = selectedLocation.boundingBoxMaxLat + ',' + selectedLocation.boundingBoxMaxLng;
                const locationBoundSW = selectedLocation.boundingBoxMinLat + ',' + selectedLocation.boundingBoxMinLng;
                updateParams.propertySearchParams.set( 'locationBoundSW', locationBoundSW );
                updateParams.propertySearchParams.set( 'locationBoundNE', locationBoundNE );
            }

            updateParams.featureSearchParams.set( 'parentType', currentLocation.type );
            updateParams.featureSearchParams.set( 'parentID', data.keys[ currentLocation.type ].id[ 0 ] );

            updateParams.featureSearchParams.set( 'locationType', currentLocation.type );
            updateParams.featureSearchParams.set( 'locationId', data.keys[ currentLocation.type ].id[ 0 ] );

            if ( searchLocationType === Coast && data.keys[ Province ] ) {
                featureTypeToLoad = Municipality;
                updateParams.featureSearchParams.set( 'parentType', Province );
                updateParams.featureSearchParams.set( 'parentID', data.keys[ Province ].id );

                updateMeta.focusMapToSearch = true;
                updateMeta.fitBounds = {
                    bbox: selectedLocation.bbox,
                    event: newBoundingBoxEvent(),
                    options: newBoundingBoxOptions(),
                };
            }

            if  ( searchLocationType === Island ) {
                featureTypeToLoad = Municipality;
                updateParams.featureSearchParams.set( 'parentType', Island );
                updateParams.featureSearchParams.set( 'parentID', searchLocationID );
            }

            if ( data.keys[ Municipality ] && data.keys[ Municipality ].id.length > 0 ) {
                const municipalityParentKey = Municipality + ':' + data.keys[ Municipality ].id[ 0 ];
                const hasMunicipalityParentAdded = propertyParams.getAll( 'parentLocations' ).some( ( key ) => key === municipalityParentKey );

                if ( !hasMunicipalityParentAdded ) {
                    propertyParams.append( 'parentLocations', municipalityParentKey );
                }
            }

            if ( featureTypeToLoad ) {
                updateParams.featureSearchURL = new URL( '/' + featureTypeToLoad, ctx.apiURL );
            } else {
                updateParams.fetchFeatures = false;
            }

            if ( currentLocation.type === Entity && data.keys[ Neighbourhood ] ) {
                updateParams.featureSearchParams.set( 'parentType', Neighbourhood );
                updateParams.featureSearchParams.set( 'parentID', data.keys[ Neighbourhood ].id[ 0 ] );
            } else if ( currentLocation.type === Entity && data.keys[ District ] ) {
                updateParams.featureSearchParams.set( 'parentType', District );
                updateParams.featureSearchParams.set( 'parentID', data.keys[ District ].id[ 0 ] );
            } else if ( !hasChildren && currentLocation.type === Entity && data.keys[ Municipality ] ) {
                updateParams.featureSearchParams.set( 'parentType', Municipality );
                updateParams.featureSearchParams.set( 'parentID', data.keys[ Municipality ].id[ 0 ] );
                updateMeta.useBoundingBoxOf = data.keys[ Entity ].id[ 0 ];
            }

            if ( siblingParentType && siblingParentType !== Coast && data.keys ) {
                let siblingMeta = {
                    focusMapToSearch: false,
                    appendFeaturesIfLayerExists: true,
                };

                switch ( siblingParentType ) {
                case Neighbourhood:
                    var nOptions = function() {
                        const nSiblingFeatureFilter = newFeatureFilter( {
                            skipBoundingBox: true,
                        } );

                        if ( data.keys[ District ] ) {
                            nSiblingFeatureFilter.set( 'parentType', District );
                            nSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ District ].id[ 0 ] ) );
                        }

                        const nSiblingOptions = {
                            fetchFeatures: true,
                            featureSearchURL: new URL( `/${Neighbourhood}`, ctx.apiURL ),
                            featureSearchParams: nSiblingFeatureFilter,
                        };

                        return {
                            options: nSiblingOptions,
                            meta: siblingMeta,
                        };
                    };

                    updateMeta.tasks.push( wrapUpdateCall( ctx, nOptions ) );

                    var ndOptions = function() {
                        const ndSiblingFeatureFilter = newFeatureFilter( {
                            skipBoundingBox: true,
                        } );

                        if ( data.keys[ Municipality ] ) {
                            ndSiblingFeatureFilter.set( 'parentType', Municipality );
                            ndSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ Municipality ].id[ 0 ] ) );
                        }

                        const ndSiblingOptions = {
                            fetchFeatures: true,
                            featureSearchURL: new URL( `/${District}`, ctx.apiURL ),
                            featureSearchParams: ndSiblingFeatureFilter,
                        };

                        return {
                            options: ndSiblingOptions,
                            meta: siblingMeta,
                        };
                    };

                    updateMeta.tasks.push( wrapUpdateCall( ctx, ndOptions ) );

                    var nmOptions = function() {
                        const nmSiblingFeatureFilter = newFeatureFilter( {
                            skipBoundingBox: true,
                        } );

                        if ( data.keys[ Province ] ) {
                            nmSiblingFeatureFilter.set( 'parentType', Province );
                            nmSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ Province ].id[ 0 ] ) );
                        }

                        const nmSiblingOptions = {
                            fetchFeatures: true,
                            featureSearchURL: new URL( `/${Municipality}`, ctx.apiURL ),
                            featureSearchParams: nmSiblingFeatureFilter,
                        };

                        return {
                            options: nmSiblingOptions,
                            meta: siblingMeta,
                        };
                    };

                    updateMeta.tasks.push( wrapUpdateCall( ctx, nmOptions ) );

                    break;

                case District:
                    var dOptions = function() {
                        const dSiblingFeatureFilter = newFeatureFilter( {
                            skipBoundingBox: true,
                        } );

                        let dSiblingPath = '';

                        if ( data.keys[ District ] ) {
                            dSiblingPath = `/${District}`;
                        } else if ( data.keys[ Neighbourhood ] ) {
                            dSiblingPath = `/${Neighbourhood}`;
                        }

                        if ( data.keys[ Municipality ] ) {
                            dSiblingFeatureFilter.set( 'parentType', Municipality );
                            dSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ Municipality ].id[ 0 ] ) );
                        }

                        const dSiblingOptions = {
                            fetchFeatures: true,
                            featureSearchURL: new URL( dSiblingPath, ctx.apiURL ),
                            featureSearchParams: dSiblingFeatureFilter,
                        };

                        return {
                            options: dSiblingOptions,
                            meta: siblingMeta,
                        };
                    };

                    updateMeta.tasks.push( wrapUpdateCall( ctx, dOptions ) );

                    if ( data.keys[ District ] ) {
                        var dnOptions = function() {
                            const dnSiblingFeatureFilter = newFeatureFilter( {
                                skipBoundingBox: true,
                            } );

                            if ( data.keys[ District ] ) {
                                dnSiblingFeatureFilter.set( 'parentType', District );

                                if ( data.keys[ District ] ) {
                                    dnSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ District ].id[ 0 ] ) );
                                }
                            }

                            const dnSiblingOptions = {
                                fetchFeatures: true,
                                featureSearchURL: new URL( `/${Neighbourhood}`, ctx.apiURL ),
                                featureSearchParams: dnSiblingFeatureFilter,
                            };

                            return {
                                options: dnSiblingOptions,
                                meta: siblingMeta,
                            };
                        };

                        updateMeta.tasks.push( wrapUpdateCall( ctx, dnOptions ) );
                    }

                    var dmOptions = function() {
                        const dmSiblingFeatureFilter = newFeatureFilter( {
                            skipBoundingBox: true,
                        } );

                        if ( data.keys[ Province ] ) {
                            dmSiblingFeatureFilter.set( 'parentType', Province );
                            dmSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ Province ].id[ 0 ] ) );
                        }

                        const dmSiblingOptions = {
                            fetchFeatures: true,
                            featureSearchURL: new URL( `/${Municipality}`, ctx.apiURL ),
                            featureSearchParams: dmSiblingFeatureFilter,
                        };

                        return {
                            options: dmSiblingOptions,
                            meta: siblingMeta,
                        };
                    };

                    updateMeta.tasks.push( wrapUpdateCall( ctx, dmOptions ) );
                    break;

                case Municipality:
                    var mOptions = function() {
                        let mSiblingFeatureFilter = newFeatureFilter();

                        if ( data.keys[ Island ] ) {
                            mSiblingFeatureFilter.set( 'parentType', Island );
                            mSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ Island ].id[ 0 ] ) );
                        } else if ( data.keys[ Province ] ) {
                            mSiblingFeatureFilter.set( 'parentType', Province );
                            mSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ Province ].id[ 0 ] ) );
                        }

                        let mSiblingOptions = {
                            fetchFeatures: true,
                            featureSearchURL: new URL( `/${siblingParentType}`, ctx.apiURL ),
                            featureSearchParams: mSiblingFeatureFilter,
                        };

                        return {
                            options: mSiblingOptions,
                            meta: siblingMeta,
                        };
                    };

                    updateMeta.tasks.push( wrapUpdateCall( ctx, mOptions ) );

                    if ( currentLocation.type === District && data.keys[ District ] ) {
                        var mdOptions = function() {
                            let mdSiblingFeatureFilter = newFeatureFilter();

                            mdSiblingFeatureFilter.set( 'parentType', Municipality );
                            mdSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ Municipality ].id[ 0 ] ) );

                            var mdSiblingOptions = {
                                fetchFeatures: true,
                                featureSearchURL: new URL( `/${District}`, ctx.apiURL ),
                                featureSearchParams: mdSiblingFeatureFilter,
                            };

                            return {
                                options: mdSiblingOptions,
                                meta: siblingMeta,
                            };
                        };

                        updateMeta.tasks.push( wrapUpdateCall( ctx, mdOptions ) );
                    }

                    var mpOptions = function() {
                        const mpSiblingFeatureFilter = newFeatureFilter();

                        mpSiblingFeatureFilter.set( 'parentType', Region );

                        if ( data.keys[ Region ] && data.keys[ Region ].id.length > 0 ) {
                            mpSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ Region ].id[ 0 ] ) );
                        }

                        let mpExcludeIDs = [];

                        if ( data.keys[ Island ] && data.keys[ Island ].id.length > 0 ) {
                            mpExcludeIDs = [ ...mpExcludeIDs, parseInt( data.keys[ Island ].id[ 0 ] ) ];
                        }

                        if ( data.keys[ Province ] && data.keys[ Province ].id.length > 0 ) {
                            mpExcludeIDs = [ ...mpExcludeIDs, parseInt( data.keys[ Province ].id[ 0 ] ) ];
                        }

                        mpSiblingFeatureFilter.set( 'excludeLocationIDs', mpExcludeIDs );

                        const bounds = ctx.map.getBounds();
                        mpSiblingFeatureFilter.set( 'boundSW', bounds.getSouthWest().lat + ',' + bounds.getSouthWest().lng );
                        mpSiblingFeatureFilter.set( 'boundNE', bounds.getNorthEast().lat + ',' + bounds.getNorthEast().lng );

                        const mpSiblingOptions = {
                            fetchFeatures: true,
                            featureSearchURL: new URL( `/${Province}`, ctx.apiURL ),
                            featureSearchParams: mpSiblingFeatureFilter,
                        };

                        return {
                            options: mpSiblingOptions,
                            meta: siblingMeta,
                        };
                    };

                    updateMeta.tasks.push( wrapUpdateCall( ctx, mpOptions ) );

                    break;

                case Province:
                    if ( searchLocationType !== Coast && searchLocationType !== Island ) {
                        var pmOptions = function() {
                            let pmSiblingFeatureFilter = newFeatureFilter();

                            if ( data.keys[ Island ] ) {
                                pmSiblingFeatureFilter.set( 'parentType', Island );
                                pmSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ Island ].id[ 0 ] ) );
                            } else {
                                pmSiblingFeatureFilter.set( 'parentType', Province );

                                if ( data.keys[ Province ] ) {
                                    pmSiblingFeatureFilter.set( 'parentID', parseInt( data.keys[ Province ].id[ 0 ] ) );
                                }
                            }

                            updateParams.featureSearchParams.set( 'parentID', searchLocationID );

                            let pmSiblingOptions = {
                                fetchFeatures: true,
                                featureSearchURL: new URL( `/${Municipality}`, ctx.apiURL ),
                                featureSearchParams: pmSiblingFeatureFilter,
                            };

                            return {
                                options: pmSiblingOptions,
                                meta: siblingMeta,
                            };
                        };

                        updateMeta.tasks.push( wrapUpdateCall( ctx, pmOptions ) );
                    }

                    if ( currentLocation.type === Island && ctx.map.getLayer( `${Region}:fill` ) ) {
                        ctx.map.setFilter( `${Region}:fill`, [
                            '!=', [ 'get', 'id' ], data.keys[ Region ].id[ 0 ],
                        ], {
                            validate: ctx.debug,
                        } );
                        ctx.map.setFilter( `${Region}:outline`, [
                            '!=', [ 'get', 'id' ], data.keys[ Region ].id[ 0 ],
                        ], {
                            validate: ctx.debug,
                        } );
                    }

                    var pOptions = () => {
                        const pSiblingFeatureFilter = newFeatureFilter( {
                            locationId: data.keys[ Province ].id[ 0 ],
                            locationType: Province,
                        } );

                        const bounds = ctx.map.getBounds();
                        pSiblingFeatureFilter.set( 'boundSW', bounds.getSouthWest().lat.toString() + ',' + bounds.getSouthWest().lng.toString() );
                        pSiblingFeatureFilter.set( 'boundNE', bounds.getNorthEast().lat.toString() + ',' + bounds.getNorthEast().lng.toString() );

                        if ( data.keys[ Island ] ) {
                            pSiblingFeatureFilter.set( 'excludeLocationIDs', [
                                parseInt( data.keys[ Province ].id[ 0 ] ),
                                parseInt( data.keys[ Island ].id[ 0 ] ),
                            ] );
                        } else if ( data.keys[ Province ] ) {
                            pSiblingFeatureFilter.set( 'excludeLocationIDs', data.keys[ Province ].id );
                        }

                        const pSiblingOptions = {
                            fetchFeatures: true,
                            featureSearchURL: new URL( `/${Province}`, ctx.apiURL ),
                            featureSearchParams: pSiblingFeatureFilter,
                        };

                        return {
                            options: pSiblingOptions,
                            meta: siblingMeta,
                        };
                    };

                    updateMeta.tasks.push( wrapUpdateCall( ctx, pOptions ) );
                    break;

                case Region:
                    var rpOptions = () => {
                        const rpSiblingFeatureFilter = newFeatureFilter();

                        if ( data.keys[ Province ] ) {
                            rpSiblingFeatureFilter.set( 'locationId', data.keys[ Province ].id[ 0 ] );
                            rpSiblingFeatureFilter.set( 'locationType', Province );
                        }

                        const bounds = ctx.map.getBounds();
                        rpSiblingFeatureFilter.set( 'boundSW', bounds.getSouthWest().lat + ',' + bounds.getSouthWest().lng );
                        rpSiblingFeatureFilter.set( 'boundNE', bounds.getNorthEast().lat + ',' + bounds.getNorthEast().lng );

                        let excludeLocationIDs = [];

                        if ( data.keys[ Province ] ) {
                            excludeLocationIDs = [ ...excludeLocationIDs, parseInt( data.keys[ Province ].id[ 0 ] ) ];
                        } else {
                            if ( ctx.map.getSource( Province ) ) {
                                excludeLocationIDs = [
                                    ...excludeLocationIDs,
                                    ctx.map.getSource( Province ).serialize().data.features.map( ( f ) => f.properties.id ),
                                ];
                            }
                        }

                        rpSiblingFeatureFilter.set( 'excludeLocationIDs', excludeLocationIDs );

                        const rpSiblingOptions = {
                            fetchFeatures: true,
                            featureSearchURL: new URL( `/${Province}`, ctx.apiURL ),
                            featureSearchParams: rpSiblingFeatureFilter,
                        };

                        return {
                            options: rpSiblingOptions,
                            meta: siblingMeta,
                        };
                    };

                    updateMeta.tasks.push( wrapUpdateCall( ctx, rpOptions ) );
                    break;
                }
            }

            let regionFillLayer = ctx.map.getLayer( `${Region}:fill` );

            if ( regionFillLayer ) {
                ctx.map.setLayoutProperty( `${Region}:fill`, 'visibility', 'none', ctx.debug );
                ctx.map.setLayoutProperty( `${Region}:outline`, 'visibility', 'none', ctx.debug );
            }

            let layersToRemove = baseLayers;

            if ( !isRadiusSearch ) {
                layersToRemove = layersToRemove.filter( ( layer ) => {
                    return layer !== Radius;
                } );
            }

            layersToRemove.forEach( ( layer ) => {
                removeLayersForSource( ctx.map, layer );

                if ( ctx.map.getSource( layer ) ) {
                    ctx.map.removeSource( layer );
                }
            } );

            updateParams.featureSearchURL = new URL( `/${featureTypeToLoad}`, ctx.apiURL );

            if ( [ Region, Province, Coast, Island ].includes( currentLocation.type ) ) {
                const loadCombinations = {
                    [ Region ]: {
                        source: Province,
                        key: 'regionID',
                    },
                    [ Province ]: {
                        source: Municipality,
                        key: 'provinceID',
                    },
                    [ Coast ]: {
                        source: Municipality,
                        key: 'coastID',
                    },
                    [ Island ]: {
                        source: Municipality,
                        key: 'islandID',
                    },
                };

                let current = loadCombinations[ currentLocation.type ];

                if ( current.source !== featureTypeToLoad ) {
                    current.source = featureTypeToLoad;
                }

                whenFeaturesAvailable( ctx.map, {
                    source: current.source,
                    target: featureTypeToLoadCount,
                    validate: ctx.debug,
                } ).then( ( features ) => {
                    for ( let i = 0; i < features.length; i++ ) {
                        if ( currentLocation.type !== Coast && features[ i ].properties[ current.key ] !== currentLocation.id ) {
                            continue;
                        }

                        if ( currentLocation.type === Coast && Array.isArray( features[ i ].properties[ current.key ] ) && !features[ i ].properties[ current.key ].includes( currentLocation.id ) ) {
                            continue;
                        }

                        if ( currentLocation.type === Coast && typeof features[ i ].properties[ current.key ] === 'number' && features[ i ].properties[ current.key ] !== currentLocation.id ) {
                            continue;
                        }

                        ctx.map.setFeatureState(
                            {
                                source: current.source,
                                id: features[ i ].id,
                            },
                            {
                                active: true,
                            } );
                    }
                } );
            }

            if ( [ Municipality, District, Neighbourhood, Entity ].includes( currentLocation.type ) ) {
                whenFeatureAvailable( ctx.map, {
                    source: currentLocation.type,
                    featureID: currentLocation.data.keys[ currentLocation.type ].id[ 0 ],
                    validate: ctx.debug,
                } ).then( ( feature ) => {
                    ctx.map.setFeatureState(
                        {
                            source: currentLocation.type,
                            id: feature.id,
                        },
                        {
                            active: true,
                        } );
                } );
            }
        }

        let results = await ctx.update( updateParams, updateMeta );

        if ( updateMeta.tasks.length > 0 ) {
            for ( let t = 0; t < updateMeta.tasks.length; t++ ) {
                const task = updateMeta.tasks[ t ];
                const result = await task( results );
                results = [ ...results, result ];
            }
        }

        if ( initialSearch && ctx.options.initialLocation.hasBbox ) {
            const initialBoundingBox = ctx.options.initialLocation.bbox;

            let isPoint = boundingBoxIsLikePoint( initialBoundingBox );
            if ( isPoint ) {
                removeApproxLocationCircle( ctx.map );

                const circleFeature = circle( initialBoundingBox[ 0 ], 0.5, {
                    units: 'kilometers',
                } );

                ctx.map.addSource( 'approx-location', {
                    type: 'geojson',
                    data: circleFeature,
                    tolerance: 0,
                } );

                ctx.map.addLayer( {
                    id: 'approx-location:fill',
                    source: 'approx-location',
                    type: 'fill',
                    paint: {
                        'fill-color': '#FFDE00',
                        'fill-opacity': 0.5,
                    },
                }, findFirstCustomLayer( ctx.map ) );
            }
        }

        if ( currentLocation && !filter.anyLocationFilters() ) {
            const backupLocation = {
                id: currentLocation.data.keys[ currentLocation.type ] ? currentLocation.data.keys[ currentLocation.type ].id[ 0 ] : 0,
                properties: {
                    id: currentLocation.data.keys[ currentLocation.type ] ? currentLocation.data.keys[ currentLocation.type ].id[ 0 ] : 0,
                    regionID: currentLocation.data.keys[ Region ] ? currentLocation.data.keys[ Region ].id[ 0 ] : 0,
                    provinceID: currentLocation.data.keys[ Province ] ? currentLocation.data.keys[ Province ].id[ 0 ] : 0,
                    islandID: currentLocation.data.keys[ Island ] ? currentLocation.data.keys[ Island ].id[ 0 ] : 0,
                    municipalityID: currentLocation.data.keys[ Municipality ] ? currentLocation.data.keys[ Municipality ].id[ 0 ] : 0,
                    type: currentLocation.type,
                    parents: data.keys,
                },
            };

            const locationSource = ctx.map.getSource( currentLocation.type );

            if ( locationSource && data.keys[ currentLocation.type ] ) {
                const srcData = locationSource.serialize();

                const locationID = data.keys[ currentLocation.type ].id[ 0 ];

                if ( srcData.data && srcData.data.features ) {
                    const location = srcData.data.features.find( ( f ) => {
                        return f.properties.id === locationID;
                    } );

                    if ( location ) {
                        ctx.currentLocation = location;
                    } else {
                        ctx.currentLocation = backupLocation;
                    }
                }
            } else {
                ctx.currentLocation = backupLocation;
            }
        }

        ctx.previousSearchState = propertyParams.toString();

        if ( initialSearch ) {
            ctx.root.dataset.initialSearchComplete = 'true';
        }

        ctx.setLoading( false );

        return results;
    }

    const hasAreaSpecified = ( propertyParams.has( 'area' ) && propertyParams.get( 'area' ).length > 0 );

    if ( hasAreaSpecified ) {
        locationSources.forEach( ( src ) => deactiveFeaturesForSource( ctx.map, src ) );

        ctx.currentLocation = null;

        if ( hasAreaSpecified && initialSearch ) {
            const requests = await getFeatureRequestsForArea( ctx, propertyParams.get( 'area' ) );

            if ( requests.length === 0 ) {
                if ( !ctx.map.getSource( Region ) ) {
                    ctx.map.addSource( Region, {
                        type: 'geojson',
                        data: ctx.JSON[ Region ],
                        tolerance: 0,
                    } );
                }

                const regionLayer = ctx.map.getLayer( `${Region}:outline` );

                if ( !regionLayer ) {
                    setLayer( ctx, {
                        name: Region,
                        source: Region,
                    } );
                } else {
                    ctx.map.setLayoutProperty( `${Region}:outline`, 'visibility', 'visible', ctx.debug );
                }
            } else {
                updateMeta.asyncTasks = [
                    ...updateMeta.asyncTasks || [],
                    ...requests,
                ];
            }
        }

        const results = await ctx.update( updateParams, updateMeta );

        ctx.previousSearchState = propertyParams.toString();

        if ( initialSearch ) {
            ctx.root.dataset.initialSearchComplete = 'true';
        }

        ctx.setLoading( false );

        return results;
    }

    const previousSearchHadArea = ( previouSearch.has( 'area' ) && previouSearch.get( 'area' ).length > 0 );

    const areaWasRemoved = ( !hasAreaSpecified && previousSearchHadArea && !hasLocationSpecified );

    const searchComponentIsActive = ctx.activeComponents.has( SearchThisArea.id );

    const doReset = (
        (
            options.forceReset ||
            ( !hasLocationSpecified && locationChanged ) ||
            areaWasRemoved
        ) &&
        !searchComponentIsActive &&
        !filter.anyLocationFilters()
    );

    if ( doReset && options.allowReset ) {
        ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:reset', {
            bubbles: true,
            detail: {
                searchOptions: options,
                form: from,
            },
        } ) );
        return;
    }

    if ( searchComponentIsActive ) {
        updateMeta.updateUI = true;
    }

    const results = await ctx.update( updateParams, updateMeta );

    ctx.previousSearchState = propertyParams.toString();

    if ( initialSearch ) {
        ctx.root.dataset.initialSearchComplete = 'true';
    }

    ctx.setLoading( false );

    return results;
}

export {
    handleSearch,
    search,
};
